import produce from "immer";
import React, { FC, useRef, useState } from "react";
import { useDrag, useDrop, XYCoord } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import {
  startAddElement,
  startDropEleemnt,
} from "../../actions/elementListAction";
import {
  startDropSection,
  startMoveSection,
} from "../../actions/sectionListAction";
import { Section } from "../../models/sectionModel";
import { AppState } from "../../reducers/rootReducer";
import {
  getElementTemplateByType,
  getElementTemplates,
} from "../../selectors/appSelectors";
import { getAllElements } from "../../selectors/elementListSelector";
import { ItemTypes } from "../../utils/itemTypes";
import RenderElement from "./RenderElement";
import Modal from "react-modal";
import { SectionList, SystemTypes } from "../../utils/constants";
import missingNumberFunc from "../utilComponents/elementNameGeneratingComponent";
import Switch from "react-switch";
import { AnyAaaaRecord } from "dns";
import ActionListComponent from "../utilComponents/actionListComponent/actionListComponent";

const customStyles = {
  content: {
    width: "25%",
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    color: "#fff",
    backgroundColor: "#222222",
  },
};

export interface SectionElementProps {
  section: Section;
  index: number;
  isPreview?: boolean;
  isTab?: boolean;
}

const SectionElement: FC<SectionElementProps> = ({
  section,
  index,
  isPreview,
  isTab,
}) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const elementState = useSelector(getAllElements);
  const elementTemplateState = useSelector(getElementTemplates);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [tabs, setTabs] = useState([{ isDropdown: false }]);

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.SECTION_MOVE,
    item: { dragItem: section, index: index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const addElement = (elementType: string, tabsCount: number, tabs?: any[]) => {
    const elemetTemplate = elementTemplateState.filter((elementTemplate) => {
      if (elementTemplate.type === elementType) {
        return elementTemplate;
      }
    });

    //filter and sort relevent element list
    const elementListCount = elementState.present
      .filter((elementList) => {
        if (elementList.type === elementType) {
          return elementList;
        }
      })
      .sort((a, b) => {
        return a.sortKey > b.sortKey ? 1 : -1;
      });

    //get missing number
    const missingNumber = missingNumberFunc(
      elementListCount.map((elementListNo) => {
        return elementListNo.sortKey;
      })
    );

    dispatch(
      startAddElement(
        {
          sectionId: section.id,
          name: `${produce(elemetTemplate[0].name, () => {})}${missingNumber}`,
          formId: section.formId,
          cellId: "",
          isSection: true,
          actions: [],
          type: elementType,
          colClass: "col-12",
          sortKey: missingNumber,
          properties: produce(elemetTemplate[0].properties, () => {}),
          styles: produce(elemetTemplate[0].styles, () => {}),
          createdTime: "",
          lastUpdatedTime: "",
        },
        tabsCount,
        tabs
      )
    );
  };

  const [{ isOver, isOverCurrent }, drop] = useDrop({
    accept: [ItemTypes.ELEMENT, ItemTypes.SECTION_MOVE, ItemTypes.ELEMENT_MOVE],
    drop(item: any, monitor) {
      const itemType = monitor.getItemType();
      if (itemType === ItemTypes.ELEMENT) {
        const didDrop = monitor.didDrop();
        if (didDrop) {
          return;
        }

        if (item.type === "tab") {
          setIsOpen(true);
        } else {
          addElement(item.type, item.type === "table" ? 5 : 1);
        }
        // const elemetTemplate = elementTemplateState.filter(
        //   (elementTemplate) => {
        //     if (elementTemplate.type === item.type) {
        //       return elementTemplate;
        //     }
        //   }
        // );

        // dispatch(
        //   startAddElement({
        //     sectionId: section.id,
        //     type: item.type,
        //     colClass: "col-12",
        //     sortKey: 0,
        //     properties: produce(elemetTemplate[0].properties, () => {}),
        //     styles: produce(elemetTemplate[0].styles, () => {}),
        //   })
        // );
      } else if (itemType === ItemTypes.ELEMENT_MOVE) {
        const didDrop = monitor.didDrop();
        if (didDrop) {
          return;
        }
        if (item.dragItem.sectionId !== section.id) {
          dispatch(startDropEleemnt(item.dragItem, section.id));
        }
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true }),
    }),
    hover(item: any, monitor) {
      const hoverType = monitor.getItemType();
      if (hoverType === ItemTypes.SECTION_MOVE) {
        if (item.dragItem.rowId === section.rowId) {
          const dragIndex = item.index;
          const hoverIndex = index;
          if (!ref.current) {
            return;
          }

          // Don't replace items with themselves
          if (dragIndex === hoverIndex) {
            return;
          }

          // Time to actually perform the action
          dispatch(startMoveSection(item.dragItem, section));

          // Note: we're mutating the monitor item here!
          // Generally it's better to avoid mutations,
          // but it's good here for the sake of performance
          // to avoid expensive index searches.
          item.index = hoverIndex;
        }
      }
    },
  });

  const renderElementList = () => {
    let elementListBySelectedSection = elementState.present.filter(
      (element) => {
        if (element.sectionId === section.id && element.isSection)
          return element;
      }
    );

    if (isPreview) {
      return elementListBySelectedSection.map((element, i) => {
        let display = element.properties?.display === true ? true : false;
        if (display)
          return (
            <RenderElement
              index={i}
              key={`${element.id}`}
              element={element}
              isPreview={isPreview}
            />
          );
      });
    } else {
      return elementListBySelectedSection.map((element, i) => {
        return (
          <RenderElement
            index={i}
            key={`${element.id}`}
            element={element}
            isPreview={isPreview}
          />
        );
      });
    }
  };

  drag(drop(ref));

  const backGroundHover = isOver
    ? isTab
      ? "drop-hover-tab"
      : "drop-hover"
    : "";
  const highlight = section.tempHiglight ? "temp-section-highlight" : "";
  const inline = section.inline ? "d-flex" : "";
  const colNameXs =
    section.responsiveXs || section.responsiveXs > 0
      ? `col-xs-${section.responsiveXs}`
      : `col-xs`;
  const colNameSm =
    section.responsiveSm || section.responsiveSm > 0
      ? `col-sm-${section.responsiveSm}`
      : `col-sm`;
  const colNameMd =
    section.responsiveMd || section.responsiveMd > 0
      ? `col-md-${section.responsiveMd}`
      : `col-md`;
  const colNameLg =
    section.responsiveLg || section.responsiveLg > 0
      ? `col-lg-${section.responsiveLg}`
      : `col-lg`;

  //custome section css - start
  const sectionStyleList = section.cssStyles;

  const sectionStyleObj: { [key: string]: any } = {};

  for (let index = 0; index < sectionStyleList.length; index++) {
    sectionStyleObj[sectionStyleList[index].retrivedStyle] =
      sectionStyleList[index].value;
  }
  //custome section css - end

  const renderCreatedTabs = () => {
    return tabs.map((tab: any, index: number) => {
      return (
        <div className="form-group row">
          <label
            htmlFor="field-disabled"
            className="col-sm-3 col-form-label font-size-12 lbl-align-right"
          >
            Tab type
          </label>
          <div className="col-sm-2 mt-1">Tab</div>
          <div className="col-sm-1 mt-1 pl-0">
            <Switch
              checked={tab.isDropdown}
              onChange={() => {
                const newTabs = produce(tabs, (draft: any) => {
                  draft[index].isDropdown = !draft[index].isDropdown;
                });
                setTabs(newTabs);
              }}
              onColor="#86d3ff"
              onHandleColor="#2693e6"
              handleDiameter={15}
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
              activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
              height={10}
              width={24}
              className="react-switch"
              id="required-field"
            />
          </div>
          <div className="col-sm-5 mt-1">Dropdown Tab</div>
        </div>
      );
    });
  };

  return (
    <>
      {!isPreview && (
        <>
          <div
            ref={ref}
            key={`${section.id}`}
            className={`${colNameXs} ${colNameSm} ${colNameMd} ${colNameLg} ${backGroundHover} ${highlight} ${
              section.sectionRowVerticalAlignment
                ? section.sectionRowVerticalAlignment
                : ""
            } ${
              section.sectionRowHorizontalAlignment
                ? section.sectionRowHorizontalAlignment
                : ""
            } ${inline}`}
            style={{ ...sectionStyleObj }}
          >
            <div
              style={{
                textAlign: "end",
                position: "absolute",
                bottom: "0px",
                right: "2px",
                fontSize: "10px",
              }}
            >
              <i className="fa fa-arrows-alt" aria-hidden="true"></i>
            </div>
            <Modal
              isOpen={modalIsOpen}
              onAfterOpen={() => {
                setTabs(() => {
                  let initTabs = [];
                  const tab = {
                    isDropdown: false,
                  };
                  initTabs.push(tab);
                  return initTabs;
                });
              }}
              onRequestClose={() => {
                setIsOpen(false);
              }}
              style={customStyles}
              contentLabel="Example Modal"
            >
              <>
                <h6>Set Tabs</h6>
                <hr className="modal-footer pb-0 pt-0" />
                {renderCreatedTabs()}
                <div className="form-group row">
                  <label
                    htmlFor="field-disabled"
                    className="col-sm-4 col-form-label font-size-12 lbl-align-right"
                  >
                    Add new tab
                  </label>
                  <div className="col-sm-2 mt-1">
                    <button
                      type="button"
                      className="btn btn-dark btn-sm font-size-12"
                      onClick={() => {
                        const tab = {
                          isDropdown: false,
                        };
                        const newTabs = produce(tabs, (draft) => {
                          draft.push(tab);
                        });
                        setTabs(newTabs);
                      }}
                    >
                      <i className="fa fa-plus"></i>
                    </button>
                  </div>
                </div>
              </>
              {/* <select
              value={tabCount}
              id="input-type"
              className="form-control"
              onChange={(e) => {
                setTabCount(parseInt(e.target.value));
              }}
            >
              {SectionList.map((item, i) => {
                return (
                  <option key={i} value={item.value}>
                    {item.name}
                  </option>
                );
              })}
            </select> */}
              <div className="modal-footer">
                <button
                  className="btn theam-btn btn-dark modal-buttons"
                  onClick={() => {
                    addElement("tab", 0, tabs);
                    setIsOpen(false);
                  }}
                >
                  <i className="fa fa-check" aria-hidden="true"></i>&nbsp;ADD
                </button>
                <button
                  className="btn theam-btn btn-dark modal-buttons"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                >
                  <i className="fa fa-times" aria-hidden="true"></i>&nbsp;CLOSE
                </button>
              </div>
            </Modal>
            {renderElementList()}
          </div>
          {/* <div key={index} className="action-list-panel">
          <ActionListComponent type={SystemTypes.ROW} section={section} />
       </div> */}
        </>
      )}
      {isPreview && (
        <div
          key={`${section.id}`}
          className={`${colNameXs} ${colNameSm} ${colNameMd} ${colNameLg} ${
            section.sectionRowVerticalAlignment
              ? section.sectionRowVerticalAlignment
              : ""
          } ${
            section.sectionRowHorizontalAlignment
              ? section.sectionRowHorizontalAlignment
              : ""
          } ${inline}`}
          style={{ ...sectionStyleObj }}
        >
          {renderElementList()}
        </div>
      )}
    </>
  );
};

export default SectionElement;
